<script setup>
/**
 * Add or edit one custom field of a ticket type.
 *
 * The key is the decision this form takes seriously. It is where the value
 * lives inside every ticket already filed under the type, so it is written once
 * and then shown read-only — renaming it would orphan everything collected so
 * far, and the server refuses to. While creating, it is suggested from the
 * label so the common case needs no thought, and stops being suggested the
 * moment the admin writes their own.
 *
 * Options only exist for the two option-backed types, which is the rule the
 * server enforces; this form mirrors it rather than inventing a second one.
 */
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  FIELD_TYPE,
  FIELD_TYPE_META,
  isOptionBackedField,
} from 'dashboard/helper/supportCaseMeta';

defineProps({
  isSubmitting: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['submit', 'close']);

const { t } = useI18n();

// The key addresses a value in a JSON map, so it stays a plain identifier: no
// spaces, no punctuation, nothing that needs quoting in a JSON path. Same
// expression the model validates with.
const KEY_FORMAT = /^[a-z0-9_]+$/;

const dialogRef = ref(null);
const editing = ref(null);
const label = ref('');
const fieldKey = ref('');
const keyTouched = ref(false);
const fieldType = ref(FIELD_TYPE.TEXT);
const options = ref([]);
const required = ref(false);

const isEditing = computed(() => Boolean(editing.value));

const typeOptions = computed(() =>
  Object.entries(FIELD_TYPE_META).map(([value, meta]) => ({
    value,
    label: t(meta.labelKey),
  }))
);

const needsOptions = computed(() => isOptionBackedField(fieldType.value));

// Turning the switch on is a rule for tickets from here on — the server does not
// retroactively invalidate the ones already filed. Said out loud at the moment of
// the flip, because the admin's fear is a wave of tickets nobody can save.
const isTurningRequiredOn = computed(
  () => required.value && !editing.value?.required
);

const filledOptions = computed(() =>
  options.value.map(option => option.trim()).filter(Boolean)
);

const canSubmit = computed(() => {
  if (!label.value.trim()) return false;
  if (!KEY_FORMAT.test(fieldKey.value)) return false;
  return !needsOptions.value || filledOptions.value.length > 0;
});

const suggestKey = value =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const onLabelInput = () => {
  if (isEditing.value || keyTouched.value) return;
  fieldKey.value = suggestKey(label.value);
};

const onKeyInput = () => {
  keyTouched.value = true;
};

const addOption = () => options.value.push('');

const removeOption = index => options.value.splice(index, 1);

const open = (definition = null) => {
  editing.value = definition;
  label.value = definition?.label || '';
  fieldKey.value = definition?.key || '';
  keyTouched.value = false;
  fieldType.value = definition?.fieldType || FIELD_TYPE.TEXT;
  options.value = [...(definition?.options || [])];
  required.value = Boolean(definition?.required);
  dialogRef.value?.open();
};

const close = () => dialogRef.value?.close();

const onSubmit = () => {
  if (!canSubmit.value) return;
  emit('submit', {
    id: editing.value?.id || null,
    payload: {
      // The key rides along on create only; the server ignores it afterwards.
      ...(isEditing.value ? {} : { key: fieldKey.value }),
      label: label.value.trim(),
      fieldType: fieldType.value,
      // Sent as an empty list for the types that have no choices, so switching
      // away from a picker clears the list rather than leaving it behind.
      options: needsOptions.value ? filledOptions.value : [],
      required: required.value,
    },
  });
};

defineExpose({ open, close });
</script>

<template>
  <Dialog
    ref="dialogRef"
    :title="
      isEditing
        ? t('SUPPORT_CASES.SETTINGS.FIELDS.EDIT_TITLE')
        : t('SUPPORT_CASES.SETTINGS.FIELDS.CREATE_TITLE')
    "
    :confirm-button-label="t('SUPPORT_CASES.SETTINGS.FORM.SAVE')"
    :cancel-button-label="t('SUPPORT_CASES.SETTINGS.FORM.CANCEL')"
    :disable-confirm-button="!canSubmit"
    :is-loading="isSubmitting"
    :close-on-click-outside="false"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col gap-4" data-testid="field-dialog">
      <Input
        v-model="label"
        :label="t('SUPPORT_CASES.SETTINGS.FIELDS.LABEL_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.FIELDS.LABEL_PLACEHOLDER')"
        data-testid="field-label"
        @input="onLabelInput"
      />

      <Input
        v-model="fieldKey"
        :label="t('SUPPORT_CASES.SETTINGS.FIELDS.KEY_LABEL')"
        :placeholder="t('SUPPORT_CASES.SETTINGS.FIELDS.KEY_PLACEHOLDER')"
        :disabled="isEditing"
        :message="
          isEditing
            ? t('SUPPORT_CASES.SETTINGS.FIELDS.KEY_LOCKED_HINT')
            : t('SUPPORT_CASES.SETTINGS.FIELDS.KEY_HINT')
        "
        message-type="info"
        data-testid="field-key"
        @input="onKeyInput"
      />

      <div class="flex flex-col gap-1">
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.TYPE_LABEL') }}
        </label>
        <Select
          v-model="fieldType"
          :options="typeOptions"
          data-testid="field-type"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.TYPE_HINT') }}
        </p>
      </div>

      <div
        v-if="needsOptions"
        class="flex flex-col gap-2"
        data-testid="field-options"
      >
        <label class="text-sm font-medium text-n-slate-12">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.OPTIONS_LABEL') }}
        </label>
        <div
          v-for="(option, index) in options"
          :key="index"
          class="flex items-center gap-2"
        >
          <Input
            v-model="options[index]"
            class="flex-1"
            :placeholder="t('SUPPORT_CASES.SETTINGS.FIELDS.OPTION_PLACEHOLDER')"
            :data-testid="`field-option-${index}`"
          />
          <Button
            variant="ghost"
            color="ruby"
            size="xs"
            icon="i-lucide-trash-2"
            :aria-label="t('SUPPORT_CASES.SETTINGS.FIELDS.OPTION_REMOVE')"
            :data-testid="`field-option-remove-${index}`"
            @click="removeOption(index)"
          />
        </div>
        <Button
          variant="outline"
          color="slate"
          size="xs"
          icon="i-lucide-plus"
          :label="t('SUPPORT_CASES.SETTINGS.FIELDS.OPTION_ADD')"
          data-testid="field-option-add"
          @click="addOption"
        />
        <p class="mb-0 text-xs text-n-slate-11">
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.OPTIONS_HINT') }}
        </p>
      </div>

      <div class="flex flex-col gap-2 p-3 border rounded-lg border-n-weak">
        <div class="flex items-start gap-3">
          <Switch v-model="required" data-testid="field-required" />
          <span class="flex flex-col gap-0.5">
            <span class="text-sm text-n-slate-12">
              {{ t('SUPPORT_CASES.SETTINGS.FIELDS.REQUIRED_LABEL') }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ t('SUPPORT_CASES.SETTINGS.FIELDS.REQUIRED_HINT') }}
            </span>
          </span>
        </div>
        <p
          v-if="isTurningRequiredOn"
          class="mb-0 text-xs text-n-amber-11"
          data-testid="field-required-warning"
        >
          {{ t('SUPPORT_CASES.SETTINGS.FIELDS.REQUIRED_WARNING') }}
        </p>
      </div>
    </div>
  </Dialog>
</template>
